import Swal from "sweetalert2";
import React, {
    useContext,
    useEffect,
    useCallback,
    useRef,
    useState
} from "react";
import * as yup from "yup";
import _ from "lodash";
import { FormikProvider, useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "@material-ui/core";

import { Typography } from "design_system/src";
import { ReactComponent as Moto } from "design_system/src/static/icons/motorcycle.svg";
import NewStepper, { IStepper } from "design_system/src/organisms/NewStepper/NewStepper/NewStepper";

import { phoneRegex, dialCodeRegex, nameRegex, curpRegex } from "constants/regex.constants";
import { LeadFormFieldsEnum, LeadStatusEnum, LeadStepEnum } from "models/lead/enums/lead.enums";
import { FinancialFormStatusEnum } from "models/financialForm/enums/financialForm.enums";

import {
    fetchGetTempToken,
    fetchValidateCurp,
    fetchValidateOzoner,
    fetchValidatePhone,
} from "models/auth-client/controllers/auth.controller";
import { createLead, updateLead, completeLead } from "models/lead/controllers/lead.controller";

import BreadCrumb from "components/breadcrumb/Breadcrumb";
import { toLeadCreate, toLeadUpdate } from "models/lead/helpers/formatData";
import { useDispatch } from "react-redux";
import { createForm } from "models/moffin/controllers/moffin.controller";
import MetaTags from "components/head/MetaTags";

import { logout } from "store/actions/user";
import { useUser } from "hooks/useUser";
import { FormApplicationContext, IStepperFormValues } from "./context/context";
import { LeadtoFormValues, FormValuesToLead } from "./helpers/transforms";

import {
    CivilStatusStep,
    AssetsStep,
    PersonalInformationStep,
    AddressStep,
    PhoneStep,
    PhoneValidationStep,
    DependantsStep,
    EmploymentAndEducationStep,
    FinancialInformationStep,
    EmailStep,
} from "./components/Steps";
import { ModalConfirmAddress } from "./components/ModalConfirmAddress/ModalConfirmAddress";
import CallToAction from "./components/CallToAction/CallToAction";

import "./FormApplication.scss";
import { ModalConfirmPhoneNumber } from "./components/ModalConfirmPhoneNumber/ModalConfirmPhoneNumber";

const validationSchema = yup.object().shape({
    [LeadStepEnum.emailStep]: yup.object().shape({
        email: yup
            .string()
            .email("Ingrese un correo valido")
            .required("Campo requerido"),
        termsOfServiceAcceptance: yup.object().shape({
            acceptedAt: yup.date().required("Campo requerido"),
            termsVersion: yup.string().required("Campo requerido"),
            acceptanceMethod: yup.string().required("Campo requerido"),
        }),
        privacyPolicyAcceptance: yup.object().shape({
            acceptedAt: yup.date().required("Campo requerido"),
            privacyPolicyVersion: yup.string().required("Campo requerido"),
            acceptanceMethod: yup.string().required("Campo requerido"),
        }),
    }),
    [LeadStepEnum.personalInformationStep]: yup.object().shape({
        name: yup
            .string()
            .required("Campo requerido")
            .matches(nameRegex, "No se permiten números ni caracteres especiales"),
        lastName: yup
            .string()
            .required("Campo requerido")
            .matches(nameRegex, "No se permiten números ni caracteres especiales"),
        curp: yup
            .string()
            .required("Campo requerido")
            .matches(curpRegex, "CURP inválido"),
    }),
    [LeadStepEnum.addressStep]: yup.object().shape({
        fullAddress: yup.object().shape({
            zipCode: yup
                .string()
                .required("Debe ingresar un codigo postal")
                .matches(/^\d{5}$/, "Debe ser un número de 5 dígitos."),
            state: yup.string().required("Campo requerido"),
            stateCode: yup.string().required("Campo requerido"),
            delegation: yup.string().required("Campo requerido"),
            neighborhood: yup.string().required("Campo requerido"),
            street: yup.string().required("Campo requerido"),
            extNumber: yup.string().min(0, "Minimo cero").required("Campo requerido"),
            intNumber: yup.string().min(0, "Minimo cero"),
            lat: yup.number(),
            long: yup.number(),
        }),
    }),
    [LeadStepEnum.phoneStep]: yup.object().shape({
        dialCode: yup
            .string()
            .matches(dialCodeRegex, "Número inválido")
            .required("Campo requerido"),
        phone: yup
            .string()
            .matches(phoneRegex, "Número inválido")
            .required("Campo requerido"),
    }),
    [LeadStepEnum.phoneValidationStep]: yup.object().shape({
        moffin: yup.object().shape({
            phoneVerifiedAt: yup.date().required("Campo requerido"),
        }),
    }),
    [LeadStepEnum.financialInformationStep]: yup.object().shape({
        monthlyIncome: yup
            .number()
            .required("Campo requerido")
            .lessThan(100001, "Debe ser menor a $100,000.00mxn")
            .positive("Debe ser mayor a $0.00mxn")
            .integer("Debe ser mayor a $0.00mxn"),
        monthlyOutcome: yup
            .number()
            .required("Campo requerido")
            .lessThan(100001, "Debe ser menor a $100,000.00mxn")
            .positive("Debe ser mayor a  $0.00mxn")
            .integer("Debe ser mayor a $0.00mxn"),
    }),
    [LeadStepEnum.dependantsStep]: yup.object().shape({
        dependantsCount: yup
            .number()
            .required("Campo requerido"),
        childrenCount: yup
            .number()
            .required("Campo requerido"),
    }),
    [LeadStepEnum.employmentAndEducationStep]: yup.object().shape({
        workInDigitalPlatforms: yup.bool(),
        economicActivity: yup.object().shape({
            value: yup.string().required("Campo requerido"),
            description: yup.string().test("", "Campo requerido", function (value) {
                if (this.parent.value === "otro") { return value ? value?.trim().length > 0 : false; }
                return true;
            }),
        }),
        companyName: yup.string(),
        educationalLevel: yup.string().required("Campo requerido"),
    }),
    [LeadStepEnum.civilStatusStep]: yup.object().shape({
        civilStatus: yup.string().required("Campo requerido"),
        livesWith: yup.array().of(yup.string()).min(1, "Al menos uno requerido"),
    }),
    [LeadStepEnum.assetsStep]: yup.object().shape({
        assets: yup.array().of(yup.string()),
    }),
});

export type StepsKeys = keyof typeof validationSchema.fields;

export const validateStep = (
    step: StepsKeys,
    values: IStepperFormValues
) => yup.reach(validationSchema, step).isValidSync(values[step as keyof IStepperFormValues]);

export const FormApplication = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const breakpoint = 900;
    const matchesMD = useMediaQuery(`(min-width:${breakpoint}px)`);
    const formRef = useRef<HTMLDivElement>(null);

    const { loading: loadingLead, step, lead, setLead, cleanLead } = useContext(FormApplicationContext);
    const [loading, setLoading] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [subcomponentCurrentStep, setSubcomponentCurrentStep] =
        useState<number>(0);
    const [openConfirmation, setOpenConfirmation] = React.useState(false);

    const { user } = useUser();
    const [allowRedirect, setAllowRedirect] = useState(false);
    const [ozonerEmail, setOzonerEmail] = useState(null);

    const handleClose = () => setOpenConfirmation(!openConfirmation);

    const handleActiveFinancialForm = (exist: boolean, financialFormStatus: any, email: string): boolean => {
        if (exist && financialFormStatus) {
            if (financialFormStatus === FinancialFormStatusEnum.REJECTED) {
                navigate("/rechazo-solicitud", {state:{ email }});
                return false;
            }
            if (
                financialFormStatus === FinancialFormStatusEnum.PENDING ||
                financialFormStatus === FinancialFormStatusEnum.CONTACTED
            ) {
                navigate(`/documentos-en-proceso?email=${encodeURIComponent(email)}`);
                return false;
            }
            navigate(
                "/solicitud-en-proceso",
                { state: { email, status: financialFormStatus } }
            );
            return false;
        }
        return true;
    };

    const onSubmitForm = async (values: IStepperFormValues) => {
        setLoading(true);
        const id = lead._id!;
        const leadData = FormValuesToLead(values, {
            ...lead,
            status: LeadStatusEnum.completed
        });

        try {
            const { ozoner, financialForm } = await completeLead(
                id,
                toLeadUpdate(leadData)
            );
            cleanLead();
            if (financialForm.status === FinancialFormStatusEnum.REJECTED) {
                navigate("/rechazo-solicitud", {state:{ email: leadData.email }});
                return;
            }
            setOzonerEmail(ozoner.email);
        } catch (e: any) {
            if (e.statusCode === 409) {
                const status = e.message.split(" ")[0];
                if (Object.values(FinancialFormStatusEnum).includes(status)) {
                    handleActiveFinancialForm(true, status, leadData.email!);
                }
            }
        } finally {
            setLoading(false);
        }
    };

    const form = useFormik<IStepperFormValues>({
        initialValues: LeadtoFormValues(lead),
        validationSchema,
        onSubmit: onSubmitForm,
        enableReinitialize: true,
    });

    const { values, errors, setFieldValue } = form;

    const createLeadFunction = async () => {
        setLoading(true);
        try {
            let leadData = FormValuesToLead(values, lead);
            if (lead._id) {
                leadData = await updateLead(lead._id, toLeadUpdate(leadData));
            } else {
                const newLeadData = await createLead(toLeadCreate(leadData));
                if (
                    (leadData.loanDuration && leadData.loanDuration !== newLeadData.loanDuration) ||
                    (leadData.vehicle && leadData.vehicle !== newLeadData.vehicle) ||
                    (leadData.advancedMoney && leadData.advancedMoney !== newLeadData.advancedMoney)
                ) {
                    leadData = await updateLead(newLeadData._id!, toLeadUpdate(leadData));
                } else {
                    leadData = newLeadData;
                }
            }
            setLead(leadData);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const updateLeadFunction = async () => {
        setLoading(true);
        try {
            let leadData = FormValuesToLead(values, lead);
            leadData = await updateLead(lead._id!, toLeadUpdate(leadData));
            setLead(leadData);
            setLoading(false);
            return true;
        } catch (error) {
            setLoading(false);
            return false;
        }
    };

    const checkinFunction = async () => {
        setLoading(true);
        const email = _.get(values, LeadFormFieldsEnum.EMAIL, "").toLowerCase();
        const {exist, financialFormStatus} = await fetchValidateOzoner({ email });
        const toContinue = handleActiveFinancialForm(
            exist,
            financialFormStatus,
            email
        );
        if (toContinue) {
            await createLeadFunction();
            setLoading(false);
        }
    };

    const curpFunction = async () => {
        setLoading(true);
        const toContinue = await updateLeadFunction();
        if (toContinue) {
            const exist = await fetchValidateCurp(_.get(values, LeadFormFieldsEnum.CURP)!);
            setLoading(false);
            return exist;
        }
        setLoading(false);
        return false;
    };

    const formatPhone = (dialCode: string, phone: string) => `${dialCode === "52" ? "521" : dialCode}${phone}`;

    const phoneFunction = async () => {
        setLoading(true);

        const currentDialCode = lead.personalInformation?.dialCode ?? "";
        const currentPhone = lead.personalInformation?.phone ?? "";
        const newDialCode = _.get(values, LeadFormFieldsEnum.DIAL_CODE)!;
        const newPhone = _.get(values, LeadFormFieldsEnum.PHONE)!;

        const phoneChanged = formatPhone(currentDialCode, currentPhone) !== formatPhone(newDialCode, newPhone);
        const exist = await fetchValidatePhone(
            _.get(values, LeadFormFieldsEnum.DIAL_CODE)!,
            _.get(values, LeadFormFieldsEnum.PHONE)!
        );
        let shouldContinue = !exist;
        const isPhoneVerified = !!lead.moffin?.phoneVerifiedAt;

        if (shouldContinue && isPhoneVerified) {
            setFieldValue(LeadFormFieldsEnum.PHONE_VALIDATION_DATE, lead.moffin?.phoneVerifiedAt);
            setLoading(false);
        }else if (shouldContinue) {
            await updateLeadFunction();
            const { isZipCodeInvalid, exceedsLimit } = await createForm(lead._id!);

            if (isZipCodeInvalid) {
                setFieldValue(LeadFormFieldsEnum.PHONE_VALIDATION_DATE, new Date());
                setActiveStep(activeStep + 1);
                shouldContinue = false;
            } else if (exceedsLimit) {
                if (phoneChanged) {
                    Swal.fire({
                        text: `Revisa si al número anterior (+${currentDialCode} ${currentPhone}) no ha llegado un SMS o contacta a un asesor para solucionar el problema.`,
                        title: `No se ha podido actualizar el número +${newDialCode} ${newPhone}`,
                        confirmButtonText: "Aceptar",
                        customClass: {
                            confirmButton: "bg_primary_300 w_90_per",
                            title: "text_neutral_800",
                            container: "text_neutral_700",
                        },
                    });

                    const prevLead = lead;
                    prevLead.personalInformation!.dialCode = currentDialCode;
                    prevLead.personalInformation!.phone = currentPhone;
                    const leadData = await updateLead(lead._id!, toLeadUpdate(prevLead));
                    setLead(leadData);
                }

                setSubcomponentCurrentStep(subcomponentCurrentStep + 1);
                shouldContinue = false;
            }
        }

        setLoading(false);
        return shouldContinue;
    };

    const validateMoffinStatus = () => {
        if (!lead.moffin?._id) {
            setFieldValue(LeadFormFieldsEnum.PHONE_VALIDATION_DATE, new Date());
        }
        form.validateForm();
        if (Object.keys(errors).length > 0) {
            Swal.fire({
                title: "Revisa de nuevo tu formulario",
                text: "Hay algunos errores en el formulario, revisa cada uno de los pasos antes de finalizar.",
                confirmButtonText: "Aceptar",
                customClass: {
                    confirmButton: "bg_primary_300 w_90_per",
                    title: "text_neutral_800",
                    container: "text_neutral_700",
                },
            });
            return false;
        }
        return true;
    };

    const stepComponentsArr: Array<IStepper>[] = [
        [
            {
                component: <EmailStep />,
                stepTitle: "Inicia tu proceso",
                validationKey: LeadStepEnum.emailStep,
                functionStep: checkinFunction
            }
        ],
        [
            {
                component: <PersonalInformationStep />,
                stepTitle: "Información personal",
                validationKey: LeadStepEnum.personalInformationStep,
                functionStep: curpFunction
            },
            {
                component: <AddressStep />,
                validationKey: LeadStepEnum.addressStep,
                stepModalConfig: {
                    widthDesktop: 50,
                    heightDesktop: 90,
                    icon: <Moto />,
                    handleClose,
                    title: <Typography scale="medium" weight="400" textColor="primary_300">
                        Confirmar Ubicación
                    </Typography>,
                    subtitle: <ModalConfirmAddress />,
                },
                functionStep: updateLeadFunction
            },
        ],
        [
            {
                component: <PhoneStep />,
                stepTitle: "Información de contacto",
                validationKey: LeadStepEnum.phoneStep,
                stepModalConfig: {
                    widthDesktop: 40,
                    heightDesktop: 40,
                    icon: <Moto />,
                    handleClose,
                    title: <Typography scale="medium" weight="400" textColor="primary_300">
                        Confirmar número telefónico
                    </Typography>,
                    subtitle: <ModalConfirmPhoneNumber />,
                },
                functionStep: phoneFunction
            },
            {
                component: <PhoneValidationStep />,
                validationKey: LeadStepEnum.phoneValidationStep,
                functionStep: updateLeadFunction
            },
        ],
        [
            {
                component: <FinancialInformationStep />,
                stepTitle: "Ingresos y gastos",
                validationKey: LeadStepEnum.financialInformationStep,
                functionStep: updateLeadFunction
            },
        ],
        [
            {
                component: <DependantsStep />,
                stepTitle: "Dependientes y empleo",
                validationKey: LeadStepEnum.dependantsStep,
                functionStep: updateLeadFunction
            },
            {
                component: <EmploymentAndEducationStep />,
                validationKey: LeadStepEnum.employmentAndEducationStep,
                functionStep: updateLeadFunction
            },
        ],
        [
            {
                component: <CivilStatusStep />,
                stepTitle: "Estado civil y Activos",
                validationKey: LeadStepEnum.civilStatusStep,
                functionStep: updateLeadFunction
            },
            {
                component: <AssetsStep />,
                validationKey: LeadStepEnum.assetsStep,
                functionStep: validateMoffinStatus
            },
        ],
    ];

    const scrollToForm = () => {
        if (formRef && formRef.current) {
            window.scrollTo({ top: formRef.current.getBoundingClientRect().top - 50, behavior: "smooth" });
        }
    };

    useEffect(() => {
        if (!loadingLead) {
            stepComponentsArr.forEach((mainStep, activeStepIndex) => {
                const array = mainStep.filter(
                    (insideStep) => insideStep.validationKey === step);
                if (array.length > 0) {
                    setActiveStep(activeStepIndex);
                    setSubcomponentCurrentStep(mainStep.indexOf(array[0]));
                }
            });
        }
    }, [loadingLead]);

    useEffect(() => {
        setLead({
            ...lead,
            step: stepComponentsArr[activeStep][subcomponentCurrentStep].validationKey as LeadStepEnum
        });
    }, [activeStep, subcomponentCurrentStep]);

    useEffect(() => {
        if (ozonerEmail) {
            if (user._id) {
                dispatch(logout());
            } else if (!user._id && !allowRedirect) {
                setAllowRedirect(true);
            } else if (allowRedirect) {
                fetchGetTempToken(ozonerEmail).then((data) => {
                    if (data.financialFormId) {
                        navigate(`/financia-tu-moto/documentos/${data.financialFormId}`);
                    }
                });
            }
        }
    }, [user, allowRedirect, ozonerEmail]);

    return (<>
        <MetaTags
            title="Motos nuevas y seminuevas a crédito sin enganche en CDMX"
            description="Compra tu moto nueva o seminueva a crédito sin enganche en Ciudad de México. Financiamiento fácil, pagos flexibles y entrega inmediata."
        />
        <div className="dso_container">
            <BreadCrumb
                activeStep={activeStep}
                setActiveStep={setActiveStep}
                stepperComponents={stepComponentsArr}
            />
            <FormikProvider value={form}>
                <div
                    style={{
                        flexDirection: matchesMD ? "row" : "column-reverse"
                    }}
                    className={
                        `display_flex flex_align_center form-application m_y_xxl${!matchesMD && activeStep > 0 ? " flex_gap_xxl" : " m_y_xl flex_gap_md"}`
                    }
                >
                    <CallToAction breakpoint={breakpoint} onClick={() => scrollToForm()} />

                    <div className="card-container dso_card" ref={formRef}>
                        <NewStepper
                            validateStep={validateStep}
                            stepperComponents={stepComponentsArr}
                            onSubmitForm={form.submitForm}
                            loading={loading}
                            activeStep={activeStep}
                            setActiveStep={setActiveStep}
                            subcomponentCurrentStep={subcomponentCurrentStep}
                            setSubcomponentCurrentStep={setSubcomponentCurrentStep}
                            startTextButton="Comenzar"
                        />
                        <div className="p_y_xs p_x_md">
                            <Typography
                            scale="xsmall"
                            weight="400"
                            className="text_center"
                            >
                            *Si detectamos información{" "}
                            <span className="text_red_300">falsa</span> o{" "}
                            <span className="text_red_300">engañosa,</span> tu solicitud
                            será inmediatamente{" "}
                            <span className="text_red_300">rechazada</span>.
                            </Typography>
                        </div>
                    </div>
                </div>
            </FormikProvider>
        </div>
    </>);
};
